/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { useState } from 'react';

import { useDebouncedCallback } from 'use-debounce';

import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Popup, type TriggerProps } from '@atlaskit/popup';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Suspense from '@atlaskit/react-ufo/suspense';
import { token } from '@atlaskit/tokens';

import { skillIconMap, type SkillIconName } from '../skill-icons';

type SkillIconComponent = (typeof skillIconMap)[SkillIconName];

export type RovoSkillTagProps = {
	// e.g. Create work items
	name: string;
	// e.g. create-work-items
	slug: string;
	description?: string;
	product?: string;
	// ADS color token string, e.g. 'var(--ds-color-accent-blue)' or a hex value
	accentColor?: string;
	// invert colors for use with dark backgrounds
	invertColors?: boolean;
	// disable hovercard functionality
	disableHovercard?: boolean;
	// Icon name from the skillIconMap
	iconName?: SkillIconName;
};

const styles = cssMap({
	parallelogram: {
		marginLeft: token('space.050'),
		paddingLeft: token('space.075'),
		paddingRight: token('space.075'),
		borderRadius: token('radius.xsmall'),
		// to create a parallelogram, skew the span -15 degrees
		transform: 'skewX(-15deg)',
		display: 'inline-block',
	},
	background: {
		backgroundColor: token('color.background.neutral'),
	},
	backgroundInverse: {
		backgroundColor: token('color.background.inverse.subtle'),
	},
	content: {
		// skew the contents of the span back to normal orientation so that only the outer element is skewed
		transform: 'skewX(15deg)',
	},
});

const hovercardStyles = cssMap({
	container: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		width: '320px',
	},
	skillName: {
		fontWeight: token('font.weight.bold'),
		marginBottom: token('space.100'),
		alignItems: 'center',
	},
	product: {
		marginTop: token('space.150'),
		color: token('color.text.subtle'),
	},
});

const getBorderColor = (accentColor?: string, invertColors?: boolean) => {
	// return the color and use it directly, otherwise return the default token
	if (accentColor) {
		return accentColor;
	} else if (invertColors) {
		return token('color.border.inverse');
	}
	return token('color.border');
};

const SkillTagInternal = ({
	triggerProps = {},
	invertColors,
	accentColor,
	slug,
	IconComponent,
	onMouseEnter,
	onMouseLeave,
}: {
	triggerProps?: Partial<TriggerProps>;
	onMouseEnter?: React.MouseEventHandler;
	onMouseLeave?: React.MouseEventHandler;
	IconComponent?: SkillIconComponent;
} & Omit<RovoSkillTagProps, 'iconName'>) => (
	<Box
		as="span"
		xcss={cx(styles.parallelogram, invertColors ? styles.backgroundInverse : styles.background)}
		style={{
			borderLeft: `${token('border.width.selected')} solid ${getBorderColor(accentColor, invertColors)}`,
		}}
		onMouseEnter={onMouseEnter}
		onMouseLeave={onMouseLeave}
		{...triggerProps}
	>
		<Inline xcss={styles.content} space="space.050" alignBlock="center">
			{IconComponent && (
				<Suspense fallback={null} interactionName="skill-tag-icon">
					<IconComponent
						label=""
						size="small"
						color={invertColors ? token('color.icon.inverse') : token('color.icon')}
					/>
				</Suspense>
			)}
			{slug}
		</Inline>
	</Box>
);

const SkillHoverCard = ({
	onMouseEnter,
	onMouseLeave,
	product,
	IconComponent,
	...props
}: {
	onMouseEnter?: React.MouseEventHandler;
	onMouseLeave?: React.MouseEventHandler;
	IconComponent?: SkillIconComponent;
} & Omit<RovoSkillTagProps, 'iconName'>) => (
	<Box xcss={hovercardStyles.container} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
		<Stack>
			<Inline xcss={hovercardStyles.skillName} space="space.100">
				{IconComponent && (
					<Suspense fallback={null} interactionName="skill-tag-hovercard-icon">
						<IconComponent label="" size="medium" color={token('color.icon.accent.blue')} />
					</Suspense>
				)}
				<Heading size="xsmall">{props.name}</Heading>
			</Inline>
			{props.description && (
				<Box>
					<Text color="color.text">{props.description}</Text>
				</Box>
			)}
			{product && (
				<Box xcss={hovercardStyles.product}>
					<Text color="color.text.subtle" size="small">
						{product}
					</Text>
				</Box>
			)}
		</Stack>
	</Box>
);

// delay opening and closing of the hovercard to prevent flickering
const SKILL_TAG_HOVERCARD_DELAY = 200;

export const SkillTag = ({
	invertColors = false,
	disableHovercard = false,
	product,
	iconName,
	...props
}: RovoSkillTagProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [setIsOpenDebounced, cancelSetIsOpen] = useDebouncedCallback((open: boolean) => {
		setIsOpen(open);
	}, SKILL_TAG_HOVERCARD_DELAY);

	const IconComponent = iconName ? skillIconMap[iconName] : undefined;
	const hasHovercardInfo = props?.name && props?.description;

	return disableHovercard || !hasHovercardInfo ? (
		<SkillTagInternal
			{...props}
			invertColors={invertColors}
			disableHovercard={disableHovercard}
			IconComponent={IconComponent}
		/>
	) : (
		<Popup
			placement="top"
			fallbackPlacements={['bottom', 'left']}
			// don't take focus away from the editor, etc.
			autoFocus={false}
			isOpen={isOpen}
			content={() => (
				<SkillHoverCard
					// cancel the close debounce when moving between the tag and the hovercard
					onMouseEnter={() => cancelSetIsOpen()}
					onMouseLeave={() => setIsOpenDebounced(false)}
					product={product}
					IconComponent={IconComponent}
					{...props}
				/>
			)}
			trigger={(triggerProps) => {
				return (
					<SkillTagInternal
						triggerProps={triggerProps}
						invertColors={invertColors}
						disableHovercard={disableHovercard}
						IconComponent={IconComponent}
						onMouseEnter={() => {
							setIsOpenDebounced(true);
						}}
						onMouseLeave={() => {
							setIsOpenDebounced(false);
						}}
						{...props}
					/>
				);
			}}
		/>
	);
};
